// libraries.
import { toast } from 'react-toastify';
import { Button } from 'react-bootstrap';
import { useEffect } from 'react';
import {
  getCountry,
  getEmail,
} from '@domains/user/store/slices/user-slice/user-slice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// slice.
import { TaskItem } from '@domains/documents-review/interfaces/interfaces';
import { setSelectedTask } from '@domains/documents-review/store/slices/document-review-slice';
import { useUpdateTaskStatusMutation } from '@domains/documents-review/store/api-slices/update-task-status.endpoints';

// icons.
import { IconDocument, IconSearch, IconWatch } from '@assets/icons';
import { TaskActionsByAgent } from './TaskActionByAgent';
import { ModelError } from '@store/apiSlices/interfaces/interfaces';
import { ReassignTaskButton } from './ReassignTaskButton';

interface TaskActionProps {
  action: string;
  nid: string;
  task: TaskItem;
  onErrorRefetch?: () => void;
}

export enum ActionTaskEnum {
  PENDING = 'pending',
  PROGRESS = 'progress',
}

export const TaskAction = ({
  action,
  nid,
  task,
  onErrorRefetch,
}: TaskActionProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const country = useSelector(getCountry);
  const email = useSelector(getEmail);

  const isAbleToStart = task.agent === email || !task.agent;

  const [mutateProgress, { isError, isLoading, isSuccess, error }] =
    useUpdateTaskStatusMutation();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (action === ActionTaskEnum.PENDING) {
      const updateTask = {
        id: task.id,
        agent: email,
        country: country,
        status: ActionTaskEnum.PROGRESS,
        nid: nid,
      };
      mutateProgress(updateTask);
      dispatch(
        setSelectedTask({
          ...task,
          agent: email,
          action: ActionTaskEnum.PROGRESS,
        })
      );
    }
  };

  const handleIfTaskIsInProgress = () => {
    dispatch(setSelectedTask(task));
    navigate(`/document-audit/task/${nid}`);
  };

  useEffect(() => {
    if (isLoading) {
      toast.loading('Iniciando tarea...', { toastId: 'loadingProgressTask' });
    } else {
      toast.dismiss('loadingProgressTask');
    }

    if (isSuccess) {
      navigate(`/document-audit/task/${nid}`);
    } else if (isError) {
      const messageFromServer =
        (error as ModelError)?.messageFromServer ??
        'Tarea en progreso por otro agente';
      toast.error(messageFromServer, {
        toastId: 'errorProgressTask',
      });
      if (onErrorRefetch) onErrorRefetch();
    }
  }, [isSuccess, isError, isLoading, navigate, nid, error]);

  const actions: Record<string, JSX.Element> = {
    Revisar: (
      <Button
        variant="link"
        onClick={handleClick}
        className="d-flex w-100 align-items-center gap-1 justify-content-center text-primary rows"
      >
        <IconSearch /> Revisar
      </Button>
    ),
    pending: (
      <div className="d-flex justify-content-center gap-1">
        <Button
          variant="link"
          className="d-flex flex-1 py-1 px-0 align-items-center gap-1 justify-content-center text-primary rows"
          onClick={handleIfTaskIsInProgress}
        >
          <IconWatch /> Ver
        </Button>
        {isAbleToStart && (
          <Button
            variant="link"
            disabled={isLoading}
            onClick={handleClick}
            className="d-flex p-1 align-items-center gap-1 justify-content-center text-primary rows"
          >
            <IconDocument /> Comenzar
          </Button>
        )}
        <ReassignTaskButton task={task} />
      </div>
    ),
    progress: (
      <div className="d-flex justify-content-center gap-1">
        <TaskActionsByAgent task={task} onViewTask={handleIfTaskIsInProgress} />
        <ReassignTaskButton task={task} />
      </div>
    ),
  };

  return actions[action] || null;
};
